import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CircleNotch, UploadSimple, DownloadSimple, CaretDown, Check } from "@phosphor-icons/react";

export default function DirectoryCover({ clubId }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ver, setVer] = useState(0);

  const load = async () => {
    try { setCfg((await api.get(`/clubs/${clubId}/directory/cover`)).data); }
    catch (_) { setCfg(null); }
  };
  useEffect(() => { if (clubId) load(); /* eslint-disable-line */ }, [clubId]);
  if (!cfg) return null;

  const d = cfg.district || {};
  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setExtra = (k, v) => setCfg((c) => ({ ...c, extra_fields: { ...(c.extra_fields || {}), [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/clubs/${clubId}/directory/cover`, {
        show_ri_president: cfg.show_ri_president,
        show_dg: cfg.show_dg,
        show_club_president: cfg.show_club_president,
        extra_fields: cfg.extra_fields || {},
      });
      toast.success("Cover saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };

  const uploadPresidentPhoto = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/clubs/${clubId}/directory/president-photo`, fd);
      toast.success("President photo uploaded"); setVer((v) => v + 1); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };

  const downloadPdf = () => window.open(`${api.defaults.baseURL}/clubs/${clubId}/directory.pdf`, "_blank");

  const Toggle = ({ testid, label, checked, onChange, hint, disabled }) => (
    <label data-testid={testid} className={`flex items-start gap-2.5 rounded-xl border p-3 ${disabled ? "opacity-60" : "cursor-pointer"}`}
      style={{ borderColor: checked ? "#5D5FEF" : "#E7E8F0", background: checked ? "#F3F3FE" : "#fff" }}>
      <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 accent-primary h-4 w-4" />
      <span className="min-w-0">
        <span className="block text-[14px] font-semibold text-foreground">{label}</span>
        {hint && <span className="block text-[12px] text-muted-foreground">{hint}</span>}
      </span>
    </label>
  );

  const presImg = `${api.defaults.baseURL}/clubs/${clubId}/directory/president-photo?v=${ver}`;

  return (
    <div className="mb-6 rounded-2xl bg-white shadow-[0_1px_2px_rgba(84,86,90,0.06)]" data-testid="directory-cover-panel">
      <button data-testid="directory-cover-toggle" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left">
        <div>
          <p className="text-[15px] font-semibold text-foreground">Directory cover</p>
          <p className="text-[13px] text-muted-foreground">Pick whose photos appear on the cover, then download the A6 directory PDF.</p>
        </div>
        <CaretDown size={18} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="space-y-6 border-t px-5 py-5" style={{ borderColor: "#EEF0F5" }}>
          {/* Whose photos */}
          <div>
            <p className="mb-2 text-[13px] font-semibold text-muted-foreground">Whose photos appear on the cover</p>
            <div className="grid gap-2.5 sm:grid-cols-3">
              <Toggle testid="toggle-ri-president" label="RI President" checked={cfg.show_ri_president}
                onChange={(v) => setField("show_ri_president", v)}
                hint={d.ri_president || d.has_ri_president_photo ? d.ri_president : "Set by the district"} />
              <Toggle testid="toggle-dg" label="District Governor" checked={cfg.show_dg}
                onChange={(v) => setField("show_dg", v)}
                hint={d.district_governor || d.has_dg_photo ? d.district_governor : "Set by the district"} />
              <Toggle testid="toggle-club-president" label="Club President" checked={cfg.show_club_president}
                onChange={(v) => setField("show_club_president", v)}
                hint={cfg.has_club_president_photo ? "Photo uploaded" : "Upload a photo below"} />
            </div>
          </div>

          {/* Additional directory fields (opt-in; RI fields are shown by default) */}
          <div data-testid="extra-fields">
            <p className="text-[13px] font-semibold text-foreground">Add extra info to the directory?</p>
            <p className="mb-2 text-[12px] text-muted-foreground">By default each entry shows the name, phone, email and mailing address from the RI file. Turn on anything extra you'd like to include — the club can fill these in when editing a member.</p>
            <div className="grid gap-2.5 sm:grid-cols-3">
              <Toggle testid="toggle-spouse" label="Spouse / partner name" checked={cfg.extra_fields?.spouse}
                onChange={(v) => setExtra("spouse", v)} hint="From each member's profile" />
              <Toggle testid="toggle-birthday" label="Birthdays" checked={cfg.extra_fields?.birthday}
                onChange={(v) => setExtra("birthday", v)} hint="Day & month only" />
              <Toggle testid="toggle-anniversary" label="Anniversaries" checked={cfg.extra_fields?.anniversary}
                onChange={(v) => setExtra("anniversary", v)} hint="Day & month only" />
            </div>
          </div>

          {/* Club president photo */}
          <div className="flex items-center gap-3">
            <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-full border" style={{ borderColor: "#E7E8F0", background: "#F4F5FA" }}>
              {(cfg.has_club_president_photo || ver > 0)
                ? <img data-testid="club-president-photo-img" src={presImg} alt="Club President" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                : <span className="text-[11px] text-muted-foreground">No photo</span>}
            </div>
            <div>
              <p className="text-[14px] font-semibold text-foreground">Club President photo</p>
              <label data-testid="upload-club-president-photo" className="mt-1 inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-semibold text-primary" style={{ borderColor: "#E7E8F0" }}>
                {busy ? <CircleNotch size={14} className="animate-spin" /> : <UploadSimple size={14} weight="bold" />} {(cfg.has_club_president_photo || ver > 0) ? "Replace photo" : "Upload photo"}
                <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => uploadPresidentPhoto(e.target.files[0])} />
              </label>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t pt-4" style={{ borderColor: "#EEF0F5" }}>
            <button data-testid="save-cover" onClick={save} disabled={busy}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-primary/90 active:scale-95 disabled:opacity-60">
              {busy ? <CircleNotch size={15} className="animate-spin" weight="bold" /> : <Check size={15} weight="bold" />} Save cover
            </button>
            <button data-testid="download-directory-pdf" onClick={downloadPdf}
              className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-[14px] font-semibold text-foreground hover:bg-black/5 active:scale-95" style={{ borderColor: "#E7E8F0" }}>
              <DownloadSimple size={15} weight="bold" /> Download directory PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
